import {
  NonInteractiveVisualizationWrapper,
  TitleSection,
} from '@mentimeter/question-modules-shared';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useRef } from 'react';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import type { QuestionWithSlide } from '@mentimeter/presentation-schema/api-types-overrides';
import type { GuessTheNumberStaticContent } from '@core-api/presentation/types/response';
import { type ScoreData } from './numerical-scale/NumericalScale';
import { NumericalQuestionVisualization } from './NumericalQuestionVisualization';
import { GuessTheNumberVisualization } from './GuessTheNumberVisualization';

interface NumericalQuestionPresentationProps {
  question: QuestionWithSlide;
  results: ScoreData | undefined;
  shouldShowCorrect: boolean;
  isScreenshotMode: boolean;
  displayMin: number;
  max: number;
  scale: number;
  errorMargin: number;
  hasErrorMargin: boolean;
  correctAnswer: number;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  isNewLookFlagEnabled: boolean;
  shouldAnimate: boolean;
}

export const NumericalQuestionPresentation = ({
  question,
  results,
  shouldShowCorrect,
  isScreenshotMode,
  displayMin,
  max,
  scale,
  errorMargin,
  hasErrorMargin,
  correctAnswer,
  CanvasVisualizationArea,
  isNewLookFlagEnabled,
  shouldAnimate,
}: NumericalQuestionPresentationProps) => {
  const contentWidthRef = useRef<HTMLDivElement | null>(null);

  const ConditionalWrapper =
    CanvasVisualizationArea ?? NonInteractiveVisualizationWrapper;

  const staticContent = question.staticContent as GuessTheNumberStaticContent;
  const interactiveContent = question.interactiveContents[0]!;

  return (
    <Box width="100%" height="100%" data-testid="numerical-question-container">
      <TitleSection
        title={question.question}
        styledTitle={question.question_styled}
        description={question.question_description}
        label={question.title_meta}
        fontSizeOffset={question.layout?.font_size_offset}
        showTextSizeDropdown={true}
      />
      <ConditionalWrapper
        width="100%"
        height="100%"
        flex="1 1 auto"
        alignItems="center"
        justifyContent="center"
        ref={contentWidthRef}
      >
        {isNewLookFlagEnabled ? (
          <GuessTheNumberVisualization
            results={results || []}
            responseRangeMin={interactiveContent.responseRange?.min || 0}
            responseRangeMax={interactiveContent.responseRange?.max || 10}
            responseRangeScale={
              staticContent.interactiveContentStyling.responseRangeScale || 10
            }
            correctAnswerRange={{
              min: interactiveContent.choices[0]!.correctAnswerRange?.min ?? 0,
              max: interactiveContent.choices[0]!.correctAnswerRange?.max ?? 1,
            }}
            shouldShowCorrect={shouldShowCorrect}
            shouldAnimate={shouldAnimate}
          />
        ) : (
          <NumericalQuestionVisualization
            question={question}
            results={results}
            shouldShowCorrect={shouldShowCorrect}
            isScreenshotMode={isScreenshotMode}
            displayMin={displayMin}
            max={max}
            scale={scale}
            errorMargin={errorMargin}
            hasErrorMargin={hasErrorMargin}
            correctAnswer={correctAnswer}
          />
        )}
      </ConditionalWrapper>
    </Box>
  );
};
